import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
// Importing the default entry injects js.stripe.com as a side effect; /pure loads
// it only when loadStripe() runs.
import { loadStripe } from '@stripe/stripe-js/pure';

import { createStripeElementsOptions } from '@/constants/stripe';
import { getDocument } from '@/lib/utils';

type Props = {
  stripePublicKey?: string | null;
  children: ReactNode;
};

export function StripeElementsProvider({ stripePublicKey = null, children }: Props) {
  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (stripePublicKey ? loadStripe(stripePublicKey) : null),
    [stripePublicKey],
  );

  const elementsOptions = useMemo(() => createStripeElementsOptions(), []);

  useEffect(() => {
    return () => {
      const doc = getDocument();
      const stripeElements = doc?.querySelectorAll('iframe[src*="stripe.com"]');
      if (stripeElements) {
        for (const element of stripeElements) {
          element.remove();
        }
      }
    };
  }, []);

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      {children}
    </Elements>
  );
}
